import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, Upload, ArrowLeftRight, TrendingUp, Coins, ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AllocationChart } from './AllocationChart';
import { totalPortfolioValue, spotBalance, spotUpnl, marginBalance, marginPositionsUpnl, marginUsedMargin, availableMargin, currentLeverage, mainAccountValue, futuresPositions, futuresUnrealizedPnl, futuresBalance, futuresAccountValue, earnBalance, earnRewards } from '../../utils/portfolioData';
// Calculate futures leverage from imported data
const futuresPositionValue = futuresPositions.reduce((sum, pos) => sum + pos.size * pos.markPrice, 0);
const futuresLeverage = futuresBalance > 0 ? futuresPositionValue / futuresBalance : 0;
// Mock data for isolated and cross margin
const isolatedMarginBalance = 3200.0;
const isolatedMarginUsed = 1280.0;
const isolatedMarginUpnl = 641.0;
const isolatedMarginPositions = 1;
const isolatedLeverage = isolatedMarginBalance > 0 ? isolatedMarginUsed / isolatedMarginBalance : 0;
const isolatedMarginHealth = isolatedMarginBalance > 0 ? (isolatedMarginBalance - isolatedMarginUsed) / isolatedMarginBalance * 100 : 0;
const crossMarginBalance = 1800.0;
const crossMarginUsed = 540.0;
const crossMarginUpnl = 157.5;
const crossMarginPositions = 1;
const crossLeverage = crossMarginBalance > 0 ? crossMarginUsed / crossMarginBalance : 0;
const crossMarginHealth = crossMarginBalance > 0 ? (crossMarginBalance - crossMarginUsed) / crossMarginBalance * 100 : 0;
// Spot 24h change calculation (mock data)
const spotChange24h = 1250.45; // Same as spotUpnl for now
const spotChange24hPercent = spotChange24h / spotBalance * 100;
// Data for different time ranges - Smoother, less volatile
const chartData = {
  '24H': [{
    time: '00:00',
    value: 48500.23,
    timestamp: '2025-01-16 00:00'
  }, {
    time: '01:00',
    value: 48623.45,
    timestamp: '2025-01-16 01:00'
  }, {
    time: '02:00',
    value: 48534.67,
    timestamp: '2025-01-16 02:00'
  }, {
    time: '03:00',
    value: 48678.89,
    timestamp: '2025-01-16 03:00'
  }, {
    time: '04:00',
    value: 48823.12,
    timestamp: '2025-01-16 04:00'
  }, {
    time: '05:00',
    value: 48912.34,
    timestamp: '2025-01-16 05:00'
  }, {
    time: '06:00',
    value: 49034.56,
    timestamp: '2025-01-16 06:00'
  }, {
    time: '07:00',
    value: 49123.78,
    timestamp: '2025-01-16 07:00'
  }, {
    time: '08:00',
    value: 49078.9,
    timestamp: '2025-01-16 08:00'
  }, {
    time: '09:00',
    value: 49234.12,
    timestamp: '2025-01-16 09:00'
  }, {
    time: '10:00',
    value: 49356.34,
    timestamp: '2025-01-16 10:00'
  }, {
    time: '11:00',
    value: 49445.56,
    timestamp: '2025-01-16 11:00'
  }, {
    time: '12:00',
    value: 49523.78,
    timestamp: '2025-01-16 12:00'
  }, {
    time: '13:00',
    value: 49612.9,
    timestamp: '2025-01-16 13:00'
  }, {
    time: '14:00',
    value: 49734.12,
    timestamp: '2025-01-16 14:00'
  }, {
    time: '15:00',
    value: 49823.34,
    timestamp: '2025-01-16 15:00'
  }, {
    time: '16:00',
    value: 49890.56,
    timestamp: '2025-01-16 16:00'
  }, {
    time: '17:00',
    value: 49945.78,
    timestamp: '2025-01-16 17:00'
  }, {
    time: '18:00',
    value: 50012.9,
    timestamp: '2025-01-16 18:00'
  }, {
    time: '19:00',
    value: 50078.12,
    timestamp: '2025-01-16 19:00'
  }, {
    time: '20:00',
    value: 50123.34,
    timestamp: '2025-01-16 20:00'
  }, {
    time: '21:00',
    value: 50167.56,
    timestamp: '2025-01-16 21:00'
  }, {
    time: '22:00',
    value: 50201.78,
    timestamp: '2025-01-16 22:00'
  }, {
    time: '23:00',
    value: 50223.9,
    timestamp: '2025-01-16 23:00'
  }, {
    time: '24:00',
    value: 50234.67,
    timestamp: '2025-01-16 24:00'
  }],
  '7D': [{
    time: 'Mon',
    value: 46823.45,
    timestamp: '2025-01-13'
  }, {
    time: 'Tue',
    value: 47912.67,
    timestamp: '2025-01-14'
  }, {
    time: 'Wed',
    value: 47234.89,
    timestamp: '2025-01-15'
  }, {
    time: 'Thu',
    value: 48567.23,
    timestamp: '2025-01-16'
  }, {
    time: 'Fri',
    value: 49345.78,
    timestamp: '2025-01-17'
  }, {
    time: 'Sat',
    value: 48923.45,
    timestamp: '2025-01-18'
  }, {
    time: 'Sun',
    value: 50234.67,
    timestamp: '2025-01-19'
  }],
  '30D': [{
    time: 'Week 1',
    value: 44567.89,
    timestamp: '2024-12-20'
  }, {
    time: 'Week 2',
    value: 47234.56,
    timestamp: '2024-12-27'
  }, {
    time: 'Week 3',
    value: 46123.45,
    timestamp: '2025-01-03'
  }, {
    time: 'Week 4',
    value: 50234.67,
    timestamp: '2025-01-10'
  }],
  '1Y': [{
    time: 'Jan',
    value: 34567.23,
    timestamp: '2024-01'
  }, {
    time: 'Feb',
    value: 36890.45,
    timestamp: '2024-02'
  }, {
    time: 'Mar',
    value: 35678.12,
    timestamp: '2024-03'
  }, {
    time: 'Apr',
    value: 38234.67,
    timestamp: '2024-04'
  }, {
    time: 'May',
    value: 40123.89,
    timestamp: '2024-05'
  }, {
    time: 'Jun',
    value: 42567.34,
    timestamp: '2024-06'
  }, {
    time: 'Jul',
    value: 41234.56,
    timestamp: '2024-07'
  }, {
    time: 'Aug',
    value: 44890.23,
    timestamp: '2024-08'
  }, {
    time: 'Sep',
    value: 46567.78,
    timestamp: '2024-09'
  }, {
    time: 'Oct',
    value: 45123.45,
    timestamp: '2024-10'
  }, {
    time: 'Nov',
    value: 48456.89,
    timestamp: '2024-11'
  }, {
    time: 'Dec',
    value: 50234.67,
    timestamp: '2024-12'
  }],
  ALL: [{
    time: '2020',
    value: 15234.78,
    timestamp: '2020'
  }, {
    time: '2021',
    value: 25678.45,
    timestamp: '2021'
  }, {
    time: '2022',
    value: 30456.89,
    timestamp: '2022'
  }, {
    time: '2023',
    value: 38123.56,
    timestamp: '2023'
  }, {
    time: '2024',
    value: 45789.23,
    timestamp: '2024'
  }, {
    time: '2025',
    value: 50234.67,
    timestamp: '2025'
  }]
};
// Custom tooltip component
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const value = payload[0].value;
  return <div style={{
    background: 'rgba(20, 22, 32, 0.65)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '28px',
    padding: '16px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
  }} data-id="element-735">
      <div style={{
      color: '#9CA3AF',
      fontSize: '11px',
      marginBottom: '8px',
      letterSpacing: '0.05em',
      fontWeight: '400',
      textTransform: 'uppercase'
    }} data-id="element-736">
        {data.timestamp}
      </div>
      <div style={{
      color: '#FFFFFF',
      fontSize: '28px',
      fontWeight: '300',
      letterSpacing: '-0.02em'
    }} data-id="element-737">
        $
        {value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}
      </div>
    </div>;
};
export function PortfolioOverview() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<keyof typeof chartData>('24H');
  const [activeAccountTab, setActiveAccountTab] = useState<'main' | 'futures'>('main');
  const [chartExpanded, setChartExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return true;
  });
  const [userOverride, setUserOverride] = useState(false);
  useEffect(() => {
    if (userOverride) return;
    const handleResize = () => {
      setChartExpanded(window.innerWidth >= 1280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userOverride]);
  const handleToggleChart = () => {
    setUserOverride(true);
    setChartExpanded(prev => !prev);
  };
  const ranges: Array<keyof typeof chartData> = ['24H', '7D', '30D', '1Y', 'ALL'];
  // Get data for selected time range
  const data = useMemo(() => {
    const rawData = chartData[timeRange];
    return rawData.map((item, index) => ({
      ...item,
      previousValue: index > 0 ? rawData[index - 1].value : item.value
    }));
  }, [timeRange]);
  // Calculate change based on time range
  const changeData = useMemo(() => {
    if (data.length < 2) return {
      amount: 0,
      percent: 0
    };
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const amount = lastValue - firstValue;
    const percent = amount / firstValue * 100;
    return {
      amount,
      percent
    };
  }, [data]);
  // Navigation handlers
  const handleMainAccountClick = () => {
    navigate('/portfolio?account=main');
  };
  const handleFuturesAccountClick = () => {
    navigate('/portfolio?account=futures');
  };
  const handleEarnAccountClick = () => {
    navigate('/portfolio?account=earn');
  };
  const handleSpotClick = () => {
    navigate('/portfolio?account=main');
  };
  const handleMarginClick = () => {
    navigate('/portfolio?account=main');
  };
  return <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 glass-border rounded-[20px] overflow-hidden p-6" data-id="element-738">
      {/* Left: Chart & Value */}
      <div className="flex flex-col h-full relative z-10" data-id="element-739">
        <div className="flex items-start justify-between mb-6" data-id="element-740">
          <div data-id="element-741">
            <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-742">
              Total Portfolio Value
            </div>
            <div className="flex items-baseline gap-2 mb-1" data-id="element-743">
              <h1 className="text-4xl font-light text-text-primary tracking-tight font-numbers" data-id="element-744">
                {totalPortfolioValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </h1>
              <span className="text-lg text-text-secondary font-normal" data-id="element-745">
                USD
              </span>
            </div>
            <span className={`flex items-center gap-1 text-sm font-medium ${changeData.amount >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-746">
              <TrendingUp size={14} className={changeData.amount < 0 ? 'rotate-180' : ''} data-id="element-747" />
              {changeData.amount >= 0 ? '+' : ''}$
              {Math.abs(changeData.amount).toLocaleString()} (
              {changeData.percent >= 0 ? '+' : ''}
              {changeData.percent.toFixed(2)}%){' '}
              <span className="text-text-secondary" data-id="element-748">{timeRange}</span>
            </span>
          </div>

          <div className="flex items-center gap-2" data-id="element-749">
            <div className="flex items-center gap-2 bg-background-elevated p-1 rounded-lg" data-id="element-750">
              {ranges.map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${timeRange === range ? 'bg-background-elevated text-text-primary border-border-default' : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-white/5'}`} data-id="element-751">
                  {range}
                </button>)}
            </div>
            <button onClick={handleToggleChart} className="p-1.5 rounded-lg bg-background-elevated border border-border-subtle hover:border-border-default text-text-secondary hover:text-text-primary transition-all" title={chartExpanded ? 'Collapse chart' : 'Expand chart'} data-id="element-752">
              {chartExpanded ? <ChevronUp size={14} data-id="element-753" /> : <ChevronDown size={14} data-id="element-754" />}
            </button>
          </div>
        </div>

        {/* Chart - shown above account cards on mobile, on right side on desktop */}
        <AnimatePresence initial={false} data-id="element-755">
          {chartExpanded && <motion.div initial={{
          height: 0,
          opacity: 0,
          marginBottom: 0
        }} animate={{
          height: 'auto',
          opacity: 1,
          marginBottom: 24
        }} exit={{
          height: 0,
          opacity: 0,
          marginBottom: 0
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} className="overflow-hidden xl:hidden" data-id="element-756">
              <div className="relative w-full max-w-[800px] h-[300px] mx-auto" style={{
            overflow: 'visible'
          }} data-id="element-757">
                <ResponsiveContainer width="100%" height="100%" data-id="element-758">
                  <AreaChart key={`mobile-chart-${timeRange}`} data={data} margin={{
                left: 10,
                right: 10,
                top: 10,
                bottom: 10
              }} style={{
                overflow: 'visible'
              }} data-id="element-759">
                    <defs data-id="element-760">
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-761">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-762" />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-763" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-764" />
                    <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                  fill: '#848E9C',
                  fontSize: 11
                }} dy={0} interval="preserveEnd" minTickGap={50} data-id="element-765" />
                    <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-766" />
                    <Tooltip content={<CustomTooltip timeRange={timeRange} data-id="element-768" />} wrapperStyle={{
                  zIndex: 9999,
                  pointerEvents: 'none'
                }} cursor={{
                  stroke: '#8B5CF6',
                  strokeWidth: 1,
                  strokeDasharray: '4 4'
                }} data-id="element-767" />
                    <Area type="monotoneX" dataKey="value" stroke="#8B5CF6" strokeWidth={1.5} fillOpacity={1} fill="url(#colorValue)" animationDuration={1000} animationEasing="ease-in-out" data-id="element-769" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Grid layout: accounts on left, chart on right (desktop only) */}
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-[minmax(340px,380px)_1fr] gap-6 min-h-[300px] overflow-visible" data-id="element-770">
          {/* Left: Account balances card + pill buttons below */}
          <div className="flex flex-col gap-3 relative" data-id="element-771">
            {/* Account Tabs */}
            <div className="flex items-center gap-1 mb-1" data-id="element-772">
              <button onClick={() => setActiveAccountTab('main')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${activeAccountTab === 'main' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-773">
                Main
              </button>
              <button onClick={() => setActiveAccountTab('futures')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${activeAccountTab === 'futures' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-774">
                Futures
              </button>
            </div>

            {/* Account balances */}
            <div className="bg-[rgba(104,107,130,0.12)] rounded-[24px] overflow-hidden p-2" data-id="element-775">
              {activeAccountTab === 'main' ? <div className="flex flex-col gap-2" data-id="element-776">
                  {/* Main Account */}
                  <button onClick={handleMainAccountClick} className="w-full p-4 hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] transition-colors text-left" data-id="element-777">
                    <div className="flex items-center justify-between mb-3" data-id="element-778">
                      <div className="text-sm font-heading text-text-primary" data-id="element-779">
                        Main Account
                      </div>
                      <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-780">
                        <Coins size={14} className="text-brand-purple-light" data-id="element-781" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-782">
                      <div className="flex items-center justify-between text-xs" data-id="element-783">
                        <span className="text-text-secondary font-label" data-id="element-784">
                          Total value
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-785">
                          {mainAccountValue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-786">USD</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-787">
                        <span className="text-text-secondary font-label" data-id="element-788">
                          Available margin
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-789">
                          {availableMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-790">USD</span>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Spot & Margin */}
                  <div className="grid grid-cols-2 gap-2" data-id="element-791">
                    {/* Spot Account */}
                    <button onClick={handleSpotClick} className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-792">
                      <div className="text-xs font-heading text-text-primary mb-2" data-id="element-793">
                        Spot
                      </div>
                      <div className="space-y-1.5" data-id="element-794">
                        <div className="flex items-center justify-between" data-id="element-795">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-796">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-797">
                            {spotBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-798">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-799">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-800">
                            Change (24h)
                          </div>
                          <div className={`font-numbers text-xs ${spotChange24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-801">
                            {spotChange24h >= 0 ? '+' : ''}
                            {spotChange24h.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-[10px] ml-0.5" data-id="element-802">
                              ({spotChange24h >= 0 ? '+' : ''}
                              {spotChange24hPercent.toFixed(2)}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Margin Account */}
                    <button onClick={handleMarginClick} className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-803">
                      <div className="flex items-center justify-between mb-2" data-id="element-804">
                        <div className="text-xs font-heading text-text-primary" data-id="element-805">
                          Margin
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-806">
                          {currentLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-807">
                        <div className="flex items-center justify-between" data-id="element-808">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-809">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-810">
                            {marginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-811">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-812">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-813">
                            Used
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-814">
                            {marginUsedMargin.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Earn Account */}
                  <button onClick={handleEarnAccountClick} className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-815">
                    <div className="text-xs font-heading text-text-primary mb-2" data-id="element-816">
                      Earn
                    </div>
                    <div className="space-y-1.5" data-id="element-817">
                      <div className="flex items-center justify-between" data-id="element-818">
                        <div className="text-[10px] text-text-secondary font-label" data-id="element-819">
                          Balance
                        </div>
                        <div className="font-numbers text-text-primary text-xs" data-id="element-820">
                          {earnBalance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-821">USD</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-822">
                        <div className="text-[10px] text-text-secondary font-label" data-id="element-823">
                          Rewards (1w)
                        </div>
                        <div className="font-numbers text-success text-xs" data-id="element-824">
                          +{earnRewards.toFixed(2)}{' '}
                          <span className="text-text-secondary" data-id="element-825">USD</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div> : <div className="flex flex-col gap-2" data-id="element-826">
                  {/* Futures Account Card */}
                  <button onClick={handleFuturesAccountClick} className="w-full p-4 hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] transition-colors text-left" data-id="element-827">
                    <div className="flex items-center justify-between mb-3" data-id="element-828">
                      <div className="text-sm font-heading text-text-primary" data-id="element-829">
                        Futures Account
                      </div>
                      <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-830">
                        {futuresLeverage.toFixed(1)}x
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-831">
                      <div className="flex items-center justify-between text-xs" data-id="element-832">
                        <span className="text-text-secondary font-label" data-id="element-833">
                          Total Balance
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-834">
                          {futuresAccountValue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-835">USD</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-836">
                        <span className="text-text-secondary font-label" data-id="element-837">
                          Unrealized P&L
                        </span>
                        <span className={`font-numbers ${futuresUnrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-838">
                          {futuresUnrealizedPnl >= 0 ? '+' : ''}
                          {futuresUnrealizedPnl.toFixed(2)}{' '}
                          <span className="text-text-secondary" data-id="element-839">USD</span>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Isolated & Cross Margin */}
                  <div className="grid grid-cols-2 gap-2" data-id="element-840">
                    {/* Isolated Margin */}
                    <button onClick={handleFuturesAccountClick} className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-841">
                      <div className="flex items-center justify-between mb-2" data-id="element-842">
                        <div className="text-xs font-heading text-text-primary" data-id="element-843">
                          Isolated
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-844">
                          {isolatedLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-845">
                        <div className="flex items-center justify-between" data-id="element-846">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-847">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-848">
                            {isolatedMarginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-849">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-850">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-851">
                            UP&L
                          </div>
                          <div className={`font-numbers text-xs ${isolatedMarginUpnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-852">
                            {isolatedMarginUpnl >= 0 ? '+' : ''}
                            {isolatedMarginUpnl.toFixed(2)}
                            <span className="text-text-secondary ml-0.5" data-id="element-853">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-854">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-855">
                            Health
                          </div>
                          <div className={`font-numbers text-xs ${isolatedMarginHealth > 50 ? 'text-success' : isolatedMarginHealth > 25 ? 'text-warning' : 'text-danger'}`} data-id="element-856">
                            {isolatedMarginHealth.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Cross Margin */}
                    <button onClick={handleFuturesAccountClick} className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-857">
                      <div className="flex items-center justify-between mb-2" data-id="element-858">
                        <div className="text-xs font-heading text-text-primary" data-id="element-859">
                          Cross
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-860">
                          {crossLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-861">
                        <div className="flex items-center justify-between" data-id="element-862">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-863">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-864">
                            {crossMarginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-865">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-866">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-867">
                            UP&L
                          </div>
                          <div className={`font-numbers text-xs ${crossMarginUpnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-868">
                            {crossMarginUpnl >= 0 ? '+' : ''}
                            {crossMarginUpnl.toFixed(2)}
                            <span className="text-text-secondary ml-0.5" data-id="element-869">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-870">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-871">
                            Health
                          </div>
                          <div className={`font-numbers text-xs ${crossMarginHealth > 50 ? 'text-success' : crossMarginHealth > 25 ? 'text-warning' : 'text-danger'}`} data-id="element-872">
                            {crossMarginHealth.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>}
            </div>

            {/* Action buttons - pill shaped, below the card */}
            <div className="flex items-center gap-2" data-id="element-873">
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors duration-200" data-id="element-874">
                <Download size={14} data-id="element-875" />
                Deposit
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors duration-200" data-id="element-876">
                <Upload size={14} data-id="element-877" />
                Withdraw
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-full transition-colors duration-200" data-id="element-878">
                <ArrowLeftRight size={14} data-id="element-879" />
                Transfer
              </button>
            </div>
          </div>

          {/* Right: Chart (desktop only) */}
          <AnimatePresence initial={false} data-id="element-880">
            {chartExpanded && <motion.div initial={{
            width: 0,
            opacity: 0
          }} animate={{
            width: 'auto',
            opacity: 1
          }} exit={{
            width: 0,
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }} className="relative h-full hidden xl:block" style={{
            overflow: 'visible'
          }} data-id="element-881">
                <ResponsiveContainer width="100%" height="100%" data-id="element-882">
                  <AreaChart key={`desktop-chart-${timeRange}`} data={data} margin={{
                left: 0,
                right: 16,
                top: 0,
                bottom: 0
              }} style={{
                overflow: 'visible'
              }} data-id="element-883">
                    <defs data-id="element-884">
                      <linearGradient id="colorValueDesktop" x1="0" y1="0" x2="0" y2="1" data-id="element-885">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-886" />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-887" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-888" />
                    <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                  fill: '#848E9C',
                  fontSize: 11
                }} dy={0} interval="preserveEnd" minTickGap={50} data-id="element-889" />
                    <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-890" />
                    <Tooltip content={<CustomTooltip timeRange={timeRange} data-id="element-892" />} wrapperStyle={{
                  zIndex: 9999,
                  pointerEvents: 'none'
                }} cursor={{
                  stroke: '#8B5CF6',
                  strokeWidth: 1,
                  strokeDasharray: '4 4'
                }} data-id="element-891" />
                    <Area type="monotoneX" dataKey="value" stroke="#8B5CF6" strokeWidth={1.5} fillOpacity={1} fill="url(#colorValueDesktop)" animationDuration={1000} animationEasing="ease-in-out" data-id="element-893" />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Right: Allocation */}
      <div className="hidden lg:block border-l border-border-subtle pl-6 relative z-0" data-id="element-894">
        <AllocationChart data-id="element-895" />
      </div>
    </div>;
}